import { riskMetrics } from '@/mocks/home';
import ScrollReveal from '@/components/base/ScrollReveal';
import SectionHeading from '@/components/base/SectionHeading';
import { useCountUp } from '@/hooks/useCountUp';

function parseMetric(value: string) {
  const match = value.match(/^([\d.]+)(.*)$/);
  if (!match) return { number: null as number | null, suffix: value };
  return { number: parseFloat(match[1]), suffix: match[2] };
}

function AnimatedValue({ value }: { value: string }) {
  const parsed = parseMetric(value);
  const { ref, value: animated } = useCountUp(parsed.number ?? 0, 1100);

  if (parsed.number === null) {
    return <span className="font-num text-2xl font-semibold text-foreground-950">{value}</span>;
  }

  const decimals = parsed.number % 1 === 0 ? 0 : 1;

  return (
    <span ref={ref} className="font-num text-2xl font-semibold text-foreground-950">
      {animated.toFixed(decimals)}
      {parsed.suffix}
    </span>
  );
}

export default function RiskSection() {
  return (
    <section className="bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-20 lg:px-10">
        <SectionHeading
          eyebrow="บริหารความเสี่ยง"
          title="การบริหารความเสี่ยงก่อนการเทรด"
          subtitle="เข้าใจความเสี่ยงก่อนตัดสินใจเปิดสถานะ เพราะการอยู่รอดในตลาดสำคัญกว่าการทำกำไรระยะสั้น"
        />

        <div className="mt-12 overflow-hidden rounded-xl border border-background-200 bg-background-100/50">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 bg-background-50 px-6 py-4">
            <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
              <i className="ri-dashboard-line text-base text-primary-600" />
              Risk Dashboard
            </span>
            <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
              ตัวอย่างการวางแผนความเสี่ยง
            </span>
          </div>

          <div className="grid grid-cols-1 divide-y divide-background-200 md:grid-cols-3 md:divide-y-0">
            {riskMetrics.slice(0, 3).map((metric, index) => (
              <ScrollReveal key={metric.label} delay={index * 70} className="md:border-r md:border-background-200">
                <div className="p-6">
                  <span
                    className={`inline-block h-1 w-8 rounded-full ${
                      metric.tone === 'accent' ? 'bg-accent-500' : 'bg-primary-500'
                    }`}
                  />
                  <p className="mt-4 text-xs font-medium uppercase tracking-wide text-foreground-500">{metric.label}</p>
                  <div className="mt-1">
                    <AnimatedValue value={metric.value} />
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{metric.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <div className="grid grid-cols-1 divide-y divide-background-200 border-t border-background-200 md:grid-cols-2 md:divide-y-0">
            {riskMetrics.slice(3).map((metric, index) => (
              <ScrollReveal
                key={metric.label}
                delay={index * 70}
                className={index === 0 ? 'md:border-r md:border-background-200' : ''}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium uppercase tracking-wide text-foreground-500">{metric.label}</p>
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                        metric.tone === 'accent'
                          ? 'bg-accent-100 text-accent-700'
                          : 'bg-primary-100 text-primary-700'
                      }`}
                    >
                      <i className="ri-shield-check-line text-base" />
                    </span>
                  </div>
                  <div className="mt-1">
                    <AnimatedValue value={metric.value} />
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{metric.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <div className="flex items-start gap-3 border-t border-background-200 bg-accent-50 px-6 py-4">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-800">
              <i className="ri-information-line text-sm" />
            </span>
            <p className="text-sm leading-relaxed text-foreground-700">
              ตัวอย่างข้างต้นเป็นเพียงแนวทางเพื่อการเรียนรู้ ไม่ใช่การรับประกันผลลัพธ์ การเทรด Forex
              มีความเสี่ยงสูง โปรดศึกษาข้อมูลและบริหารความเสี่ยงอย่างรอบคอบเสมอ
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}